import { ChangeEvent, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import {
  AccountPhoneNumberStatusesEnum,
  BrokerLicenseStatusesEnum,
  ServerErrors,
} from "dash-common";
import {
  Box,
  Button,
  Flex,
  Form,
  FormContext,
  FormErrorTypes,
  Icon,
  Input,
  InputPhoneValue,
  Select,
  Text,
  toast,
  useScreenResolution,
  useServerResponseTranslation,
} from "dash-ui";

import { FormFields } from "pages/ProfilePage/components/AccountInfo/components/AccountInfoForm/AccountInfoForm.constants";
import { useValidation } from "pages/ProfilePage/components/AccountInfo/components/AccountInfoForm/AccountInfoForm.hooks";
import {
  BrokerFragment,
  GetBrokerForAccountManagementDocument,
  useGetUsStatesQuery,
  useSendVerificationCodeMutation,
  useVerifyPhoneNumberAndUpdateAccountMutation,
} from "gql/generated.graphql";

import { BROKER_LICENSE_PROPS } from "./MainBrokerInfo.constants";
import { IconWrapper, ValidationTextWrapper } from "./MainBrokerInfo.styles";

const MainBrokerInfo = ({ broker }: { broker?: BrokerFragment }) => {
  const theme = useTheme();
  const { t } = useTranslation("forms");
  const { formState, stateHandlers, formErrors, errorHandlers } =
    useContext(FormContext);

  const validateFieldByName = useValidation();
  const {
    devices: { isMobile, isTablet, isLaptop },
  } = useScreenResolution();
  const isResponsive = isMobile || isTablet || isLaptop;
  const brokerLicenseProps =
    BROKER_LICENSE_PROPS[
      formState.brokerLicenseStatus as BrokerLicenseStatusesEnum
    ];

  const translateErrorMessage = useServerResponseTranslation();

  const { data: statesData } = useGetUsStatesQuery();

  const statesOptions = useMemo(() => {
    const states = statesData?.getUSStates || [];
    return states.map(({ name, code }) => ({
      value: code,
      label: name,
    }));
  }, [statesData]);

  const { isPhoneNumberValid, isPhoneNumberVerified } = useMemo(
    () => ({
      isPhoneNumberValid:
        formState.phoneNumber === broker?.account.phone?.number &&
        formState.phoneNumberDialCode === broker?.account.phone?.dialCode,
      isPhoneNumberVerified:
        formState.phoneNumberStatus === AccountPhoneNumberStatusesEnum.VERIFIED,
    }),
    [broker, formState]
  );

  const phoneHasChanged = useMemo(
    () =>
      formState.phoneNumber !== broker?.account.phone?.number ||
      formState.phoneNumberDialCode !== broker?.account.phone?.dialCode,
    [broker, formState]
  );

  const handleInputValue =
    (field: FormFields) => (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      stateHandlers[field](value);
      validateFieldByName(field, value);
    };

  const onCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    stateHandlers[FormFields.VerifyPhoneNumberCode](value);
    validateFieldByName(FormFields.VerifyPhoneNumberCode, value);
  };

  const handlePhoneValue = ({
    dialCode,
    number,
    isUSCodeSelected,
  }: InputPhoneValue) => {
    stateHandlers[FormFields.PhoneNumberDialCode](dialCode);
    stateHandlers[FormFields.PhoneNumber](number);
    validateFieldByName(FormFields.PhoneNumberDialCode, dialCode as string);

    if (!isUSCodeSelected) {
      stateHandlers[FormFields.BrokerLicenseNumber]("");
      stateHandlers[FormFields.State]("");
    }
  };

  return (
    <Box
      ml={[0, 8, 8, 8]}
      w="100%"
      maxWidth={["100%", "100%", "100%", "100%"]}
      mt={[4, 0, 0, 0]}
    >
      <Flex mb={isMobile ? 4 : 6} flexDirection={isMobile ? "column" : "row"}>
        <Form.Group w="100%" mr={8} mb={isMobile ? 4 : 0}>
          <Form.Label required>{t("labels.firstName")}</Form.Label>
          <Form.Input
            placeholder={t("placeholders.firstName") || ""}
            value={formState[FormFields.FirstName] || ""}
            onChange={handleInputValue(FormFields.FirstName)}
            $errorType={formErrors[FormFields.FirstName]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.FirstName]?.type}>
            {formErrors[FormFields.FirstName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%">
          <Form.Label required>{t("labels.lastName")}</Form.Label>
          <Form.Input
            placeholder={t("placeholders.lastName") || ""}
            value={formState[FormFields.LastName] || ""}
            onChange={handleInputValue(FormFields.LastName)}
            $errorType={formErrors[FormFields.LastName]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.LastName]?.type}>
            {formErrors[FormFields.LastName]?.message}
          </Form.Feedback>
        </Form.Group>
      </Flex>
      <Flex
        mb={formState.verifyPhoneNumber ? 0 : 4}
        flexDirection={isMobile ? "column" : "row"}
      >
        <Form.Group w="100%" mr={8} mb={isMobile ? 4 : 0}>
          <Form.Label>{t("labels.phoneNumber")}</Form.Label>
          <Flex>
            <Box w="100%" pos="relative">
              <Form.InputPhone
                w="100%"
                placeholder={t("placeholders.phoneNumber") || ""}
                dialCode={formState[FormFields.PhoneNumberDialCode] || ""}
                value={formState[FormFields.PhoneNumber] || ""}
                onChangePhoneValue={handlePhoneValue}
                $errorType={formErrors[FormFields.PhoneNumber]?.type}
              />
            </Box>
          </Flex>
          <Form.Feedback $errorType={formErrors[FormFields.PhoneNumber]?.type}>
            {formErrors[FormFields.PhoneNumber]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%">
          <Form.Label>{t("labels.brokerLicense")}</Form.Label>
          <Flex flexDirection="column" w="100%" h="fit-content">
            <Form.Input
              value={formState.brokerLicenseNumber || ""}
              onChange={handleInputValue(FormFields.BrokerLicenseNumber)}
              iconRight={brokerLicenseProps?.icon}
              iconRightProps={{ color: brokerLicenseProps?.color, size: 20 }}
              placeholder={t("placeholders.brokerLicenseNumber") || ""}
              $errorType={formErrors[FormFields.BrokerLicenseNumber]?.type}
            />
            <div>
              <Text color={brokerLicenseProps?.color}>
                {brokerLicenseProps?.label}
              </Text>
            </div>
            <Form.Feedback
              $errorType={formErrors[FormFields.BrokerLicenseNumber]?.type}
            >
              {formErrors[FormFields.BrokerLicenseNumber]?.message}
            </Form.Feedback>
          </Flex>
        </Form.Group>
      </Flex>

      <Flex
        mb={isMobile ? 4 : 6}
        justifyContent="flex-end"
        flexDirection={isMobile ? "column" : "row"}
      >
        <Form.Group w={isMobile ? "100%" : "calc(50% - 16px)"}>
          <Form.Label>{t("forms:labels.state")}</Form.Label>
          <Select
            w="100%"
            value={formState.state}
            options={statesOptions}
            onChanged={(value) => stateHandlers[FormFields.State](value)}
          />
        </Form.Group>
      </Flex>
      <Flex mb={isMobile ? 4 : 6} flexDirection={isMobile ? "column" : "row"}>
        <Form.Group w="100%" mr={8} mb={isMobile ? 4 : 0}>
          <Form.Label>{t("labels.agencyName")}</Form.Label>
          <Form.Input
            placeholder={t("placeholders.agencyName") || ""}
            value={formState[FormFields.AgencyName] || ""}
            onChange={handleInputValue(FormFields.AgencyName)}
            $errorType={formErrors[FormFields.AgencyName]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.AgencyName]?.type}>
            {formErrors[FormFields.AgencyName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%">
          <Form.Label>{t("labels.agencyWebsite")}</Form.Label>
          <Form.Input
            placeholder={t("placeholders.url") || ""}
            value={formState[FormFields.AgencyWebsite] || ""}
            onChange={handleInputValue(FormFields.AgencyWebsite)}
            $errorType={formErrors[FormFields.AgencyWebsite]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[FormFields.AgencyWebsite]?.type}
          >
            {formErrors[FormFields.AgencyWebsite]?.message}
          </Form.Feedback>
        </Form.Group>
      </Flex>
    </Box>
  );
};

export default MainBrokerInfo;
